import { CliVerificationMethod, CliVerificationStatus } from '../models/cli-verification.model';

/** The steps of the guided setup, in the only order they can happen in. */
/**
 * `intro` is step ZERO and `confirmation` the last one. Both were missing from the rail until
 * 2026-09-11: the customer stood on a screen the progress line did not acknowledge, and the
 * confirmation was numbered as if it were the booking itself.
 *
 * Zero-based on purpose (Torsten): the intro informs, it does not ask for anything, so counting it
 * as the first thing to DO would overstate what the flow costs.
 */
export type SetupStep =
	| 'intro'
	| 'signIn'
	| 'chooseNumber'
	| 'orderSipAccount'
	| 'verifyNumber'
	| 'confirmation';

/**
 * Which of the agreed cases this payload falls into (Torsten, 2026-10-09). The matrix is in the
 * spec; this is the same thing as a number so a Graylog dashboard can group by it.
 *
 * 1 extension orderable, none held, no number   4 extension NOT orderable, none held, no number
 * 2 extension held, no number                   5 extension NOT orderable, none held, number set
 * 3 extension orderable, none held, number set  6 nothing missing - the dialog does not open
 * 7 extension held, number set but not proven (verification NONE or PENDING)
 *
 * Cases 5 and 6 both open nothing, and they are not the same situation: 6 is a customer who needs
 * nothing, 5 is a customer this flow cannot help. Keeping them apart is most of why this is a
 * number and not a boolean.
 */
export type SetupCase = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface SetupRequirement {
	/** Which case of the agreed matrix this is. See {@link SetupCase}. */
	setupCase: SetupCase;
	/** No SIP number and no extension — calls cannot be placed at all. Chargeable to fix. */
	needsSipAccount: boolean;
	/** No number to show the person being called. Free to fix. */
	needsOutgoingNumber: boolean;
	/**
	 * Consent to the contract — AGB plus the § 71 Abs. 3 TKG declaration. Needed
	 * exactly when the order is: it is consent *to that order*, so it cannot be required without one.
	 * It is not a step of its own — it lives ON the order screen, above the confirm button, as the
	 * prototype has it.
	 */
	needsConsent: boolean;
	/**
	 * Whether the run ends with proving the number by a verification call. Only possible where the
	 * SIP trunk extension is booked or about to be - the server refuses to call otherwise. Whether
	 * the call is actually needed is settled at run time (the number may already be verified for
	 * the customer); this only says the step belongs to the flow.
	 */
	offersVerification: boolean;
	/** Only the steps this customer actually has to walk through. */
	steps: SetupStep[];
	/** Whether the guided setup has to appear at all. */
	isBlocked: boolean;
}

/** Only the fields of the callnote payload this decision reads. */
export interface SetupSignals {
	/** `null` arrives from JSON and means the same as absent: unknown. */
	hasSipNumber?: boolean | null;
	hasSipExtension?: boolean | null;
	outboundOutgoingPhoneNumber?: string | null;
	/**
	 * The monthly fee, and with it whether the extension can be ordered at all.
	 *
	 * - a number (`0` included) — the plan carries the extension, it is orderable
	 * - `null` — the plan does NOT carry it. Confirmed as the backend contract by Torsten,
	 *   2026-10-09: "if price >= 0.0 the extension is available, if price == null it is not"
	 * - `undefined` — the payload did not say. Treated exactly like `null`, because the alternative
	 *   is incoherent on screen: offering the order while the summary says the price is unavailable.
	 *   One reading of this field, used for both the figure and the routing. The failure mode if a
	 *   backend ever stops sending it is that nobody can order — loud and obvious, which is the
	 *   right direction for a field that decides whether a chargeable contract is offered.
	 */
	sipExtensionPrice?: number | null;
	/**
	 * The ISO-4217 code the price is billed in, from the brand. Part of the routing since M3 (code
	 * review 2): a price that cannot be SHOWN is not offered - see {@link sipAccountIsOnOffer}.
	 */
	currency?: string | null;
	/**
	 * Whether the configured number is proven, from phoneservice via the callnote. `null` means no
	 * number is configured OR phoneservice could not be asked - unknown, so it never opens the
	 * dialog on its own.
	 */
	cliVerificationStatus?: CliVerificationStatus | null;
	cliVerificationMethod?: CliVerificationMethod | null;
}

/**
 * PS-6495 — decides what the customer is actually missing.
 *
 * The two prerequisites are independent: most customers lack exactly one. Deriving them separately
 * is what keeps the flow from telling someone to book an account they already have.
 *
 * **Absent SIP flags never trigger the order, and that asymmetry is a rule, not caution.**
 * `hasSipNumber` and `hasSipExtension` arrived with PS-6494; a payload without them says nothing
 * about whether an account exists. Guessing "missing" would book a second billed SIP-Account for a
 * customer who already has one — so this fails **closed** on the chargeable step. The outgoing
 * number is free to set and harmless to offer, so it fails **open**: absent counts as blank.
 *
 * Order is fixed rather than a preference: signing in gates everything after it, and the number is
 * settled before the order so the order summary can state what it will actually signal. Consent is
 * not a step — it sits on the order screen, above the button that concludes the contract.
 */
export function deriveSetupRequirement(signals: SetupSignals): SetupRequirement {
	const sipAccountMissing = sipAccountKnownMissing(signals);

	/*
	 * A missing SIP-Anschluss is only something this flow can FIX while the plan actually carries
	 * the extension. `sipExtensionPrice === null` means it does not (PS-6526's contract, confirmed
	 * by Torsten on 2026-10-09), and offering the order anyway would walk the customer to a confirm
	 * button that concludes a chargeable contract the tariff cannot support.
	 *
	 * Until 2026-10-09 the price was read for the DISPLAY only — the order summary said "Für Ihren
	 * Tarif nicht verfügbar" where the figure goes and then offered the order underneath it. The
	 * DTO's own comment already said to hide the offer; only the figure was hidden.
	 *
	 * What happens instead: the run keeps whatever else it can do. If a number is also missing the
	 * customer still sets one and the callback goes out; if nothing else is missing the dialog does
	 * not open at all and the callback takes the ordinary path, where support picks up what the
	 * customer cannot fix themselves.
	 */
	const needsSipAccount =
		sipAccountMissing && sipAccountIsOnOffer(signals.sipExtensionPrice, signals.currency);
	const needsOutgoingNumber = !isPresent(signals.outboundOutgoingPhoneNumber);
	const trunkHeld = signals.hasSipExtension === true;
	const offersVerification = needsSipAccount || trunkHeld;
	const configuredNumberUnproven =
		trunkHeld && !needsOutgoingNumber && isUnproven(signals.cliVerificationStatus);

	const steps: SetupStep[] = [];
	if (needsSipAccount || needsOutgoingNumber || configuredNumberUnproven) {
		steps.push('intro');
		/*
		 * EVERY run signs in — SIP-Anschluss, extension or number alike (Torsten, 2026-10-02). This
		 * reverses the 2026-09-11 decision that tied the sign-in to the order.
		 *
		 * The old reasoning was technical and still holds on its own terms: the chosen number travels
		 * in the callback payload to `POST /document/v1/outboundtasks/callbackoncallnote?uid=…`,
		 * which carries no `@PreAuthorize` and gets no bearer, so the number-only path needs no token
		 * to FUNCTION. What it does not answer is who may set it: that number becomes the caller ID
		 * on every callback, and leaving it uid-authorised was recorded as an accepted risk rather
		 * than a safe design. Requiring the sign-in closes it.
		 *
		 * Consequence worth knowing: `check_token` now runs on the number-only path too, so a token
		 * that resolves no single phone service can fail a run that previously could not fail there.
		 *
		 * THE SECOND REASON, and the one that should stop this being reversed again (Torsten,
		 * 2026-10-09): the follow-up story adds ownership verification of the entered number
		 * (PS-6391), and that cannot be started without a signed-in customer. The payload's own
		 * `cliVerificationStatus` schema says so outright — "Verification cannot be started from this
		 * page: the customer has to be logged in for that." Without the sign-in here, PS-6391 would
		 * be a rebuild of this flow rather than an addition to it.
		 *
		 * So the sign-in is load-bearing twice over: it answers WHO may set the caller ID today, and
		 * it is the precondition for proving the number belongs to them tomorrow. This was decided
		 * the other way round once, on 2026-09-11, which is why both reasons are written down.
		 */
		steps.push('signIn');
		// THE NUMBER COMES BEFORE THE ORDER. Torsten's call (2026-09-04), and it is the prototype's
		// own sequence: login, enter number, then confirm the order. It also makes the order screen
		// honest — the number it says it will signal is already known by the time it is shown, so
		// there is no "will be decided later" placeholder in a contract summary.
		/*
		 * Shown even when a number is already configured (Torsten, 2026-09-11). Skipping it hid the
		 * single most consequential value in the run — the number every callback will signal — behind
		 * a summary row on the order screen. Now it is a step of its own, pre-filled, so the customer
		 * confirms it rather than discovers it.
		 */
		steps.push('chooseNumber');
		if (needsSipAccount) {
			steps.push('orderSipAccount');
		}
		if (offersVerification) {
			steps.push('verifyNumber');
		}
		// Only a booking or a verification produces something to confirm. When just the number was
		// missing on an account without the trunk, the run ends on that step and the callback goes
		// out, so a confirmation dot would promise a screen that never comes.
		if (needsSipAccount || offersVerification) {
			steps.push('confirmation');
		}
	}

	return {
		setupCase: toSetupCase(sipAccountMissing, needsSipAccount, needsOutgoingNumber, configuredNumberUnproven),
		needsSipAccount,
		needsConsent: needsSipAccount,
		offersVerification: steps.includes('verifyNumber'),
		needsOutgoingNumber,
		steps,
		isBlocked: steps.length > 0,
	};
}

/**
 * `sipAccountMissing` is "no usable account"; `orderable` is that AND the plan carrying the
 * extension. The pair is what separates case 1 from 4 and case 3 from 5 - without the first of the
 * two, a customer the flow cannot help would be indistinguishable from one who needs nothing.
 */
function toSetupCase(
	sipAccountMissing: boolean,
	orderable: boolean,
	needsOutgoingNumber: boolean,
	configuredNumberUnproven: boolean,
): SetupCase {
	if (!sipAccountMissing) {
		if (needsOutgoingNumber) {
			return 2;
		}
		return configuredNumberUnproven ? 7 : 6;
	}
	if (orderable) {
		return needsOutgoingNumber ? 1 : 3;
	}
	return needsOutgoingNumber ? 4 : 5;
}

/**
 * PS-6495 — the "special case": the payload reports no usable SIP-Account (SIP number and
 * extension both explicitly missing) while an outgoing number IS already configured. Setup cases 3
 * and 5 of {@link SetupCase}; only case 3 opens the dialog, because case 5 has nothing to fix.
 *
 * Before this ticket that payload went straight to `createCallback()` with the configured number
 * (origin/main, `triggerOutboundCall`). The developer's decision of 2026-10-12: such a customer is
 * NOT offered the "contact service" escape after a sign-in problem — the failure screen behaves as
 * it did before that escape existed. A refused or unconfirmed booking still ends in the contact
 * callback, because at that point an order was attempted and somebody has to follow it up.
 *
 * Fails closed like the order itself: absent SIP flags say nothing, so they are not this case.
 */
export function isNumberSetWithoutSipAccount(signals: SetupSignals): boolean {
	return sipAccountKnownMissing(signals) && isPresent(signals.outboundOutgoingPhoneNumber);
}

/**
 * "No usable SIP-Account" only when the payload SAYS so for both: `hasSipNumber === false` AND
 * `hasSipExtension === false`. Fails closed (L4, code review 2): a JSON `null`, an absent flag, or a
 * payload carrying only one of the two is unknown, and unknown never offers the billed order. Either
 * flag `true` is a usable account, as before.
 */
function sipAccountKnownMissing(signals: SetupSignals): boolean {
	return signals.hasSipNumber === false && signals.hasSipExtension === false;
}

/** VERIFIED and unknown (`null`/absent) both leave the dialog closed. */
function isUnproven(status: CliVerificationStatus | null | undefined): boolean {
	return status === 'NONE' || status === 'PENDING';
}

/** Blank and whitespace-only both mean "no number is configured". */
function isPresent(value: string | null | undefined): boolean {
	return typeof value === 'string' && value.trim().length > 0;
}

/** E.164 allows at most 15 digits; below seven nothing reaches a subscriber. */
const MIN_DIGITS = 7;
const MAX_DIGITS = 15;

/**
 * PS-6495 — is this typed entry shaped like a phone number at all?
 *
 * A FORMAT check and nothing more, asked for on 2026-09-11. The policy check - landline, blocked
 * range, ownership - is phoneservice's `number/validate`, which the number step calls. A thin
 * client-side pre-check for the unambiguous cases (foreign, mobile, 032) sits in
 * `precheckPhoneNumber` purely for instant feedback (Torsten, 2026-10-14); the backend stays the
 * authority.
 *
 * What it is for: the number becomes the caller ID on every callback, and a typo produces calls
 * nobody can return. Rejecting input that cannot be a number catches that at the only moment the
 * customer can still fix it.
 *
 * Separators are accepted because people type them (`030 / 123-456`), and a validator that refuses
 * the way its users write is a validator they work around.
 */
export function isPlausiblePhoneNumber(value: string): boolean {
	const trimmed = value.trim();
	if (trimmed.length === 0) {
		return false;
	}
	// A leading + is allowed, anywhere else it is not a number.
	if (!/^\+?[0-9 ()/.‐-―-]+$/.test(trimmed)) {
		return false;
	}
	const digits = normalizePhoneNumber(trimmed).replace(/\D/g, '');
	return digits.length >= MIN_DIGITS && digits.length <= MAX_DIGITS;
}

/**
 * The number as it is SENT (L5, code review 2): separators people type - spaces, dashes, slashes,
 * dots, parentheses - removed, and the German-style trunk `(0)` after an international prefix
 * dropped, because `+49 (0)30 …` is dialled as `+4930…`. Without a `+` prefix a `(0…)` is the
 * national trunk itself (`(030) 123`) and only its parentheses go.
 *
 * Idempotent, so a number that is already clean - every backend-delivered candidate - is unchanged.
 */
export function normalizePhoneNumber(value: string): string {
	let number = value.trim();
	if (number.startsWith('+')) {
		number = number.replace(/\(\s*0\s*\)/g, '');
	}
	return number.replace(/[\s()/.‐-―-]/g, '');
}

/**
 * PS-6526 — is the SIP extension on offer for this customer at all?
 *
 * `sipExtensionPrice: null` does NOT mean "we could not read the price". It means the plan the
 * contract runs on does not offer the extension, OR the fee could not be resolved upstream — the
 * backend cannot tell those apart and says so. Either way the honest rendering is the same: no
 * offer, rather than an offer without a figure.
 *
 * M3 (code review 2): the SAME predicate as the display. A price without a usable currency renders
 * no figure (`formatMonthlyPrice` returns null), and a billed order must not be offered under a
 * summary that says the price is unavailable (`sip_order_price_unavailable`). Such a payload is
 * treated as not on offer, so it lands in case 4 or 5 of "Which case a customer lands in" - the
 * number step, or no dialog, and support picks up the rest - rather than at a confirm button with
 * no price above it.
 *
 * `0` is NOT null: a free offer is a real offer and must survive this check.
 */
export function sipAccountIsOnOffer(
	price: number | null | undefined,
	currency: string | null | undefined,
): boolean {
	return isDisplayablePrice(price, currency);
}

/** A finite number and a currency code `Intl` accepts. Locale-independent on purpose. */
function isDisplayablePrice(
	price: number | null | undefined,
	currency: string | null | undefined,
): price is number {
	if (typeof price !== 'number' || !Number.isFinite(price) || !currency) {
		return false;
	}
	try {
		new Intl.NumberFormat(FALLBACK_LOCALE, { style: 'currency', currency });
		return true;
	} catch {
		/** A malformed currency code makes Intl throw a RangeError. */
		return false;
	}
}

/** The locale the price is formatted in when the browser's own tag is unusable. */
const FALLBACK_LOCALE = 'de-DE';

/**
 * PS-6526 — the monthly price as the customer should read it.
 *
 * Formatting lives here because the backend stopped doing it on 2026-09-21: it sends a number plus
 * an ISO-4217 code (`4.9` + `"EUR"`), not a ready-made string. `Intl` rather than a hand-rolled
 * formatter, because the minor unit is per-currency — brands bill in EUR, GBP, CHF, THB and JPY,
 * and JPY has no decimals at all. **Never default the currency:** a figure in the wrong one is a
 * legally wrong price in front of a customer, which is why a missing code renders nothing.
 *
 * Returns null exactly when {@link sipAccountIsOnOffer} is false, so routing and display cannot
 * disagree: an unusable LOCALE falls back to de-DE instead of hiding a price the routing offers.
 */
export function formatMonthlyPrice(
	price: number | null | undefined,
	currency: string | null | undefined,
	locale: string,
): string | null {
	if (!isDisplayablePrice(price, currency)) {
		return null;
	}
	const options: Intl.NumberFormatOptions = { style: 'currency', currency: currency as string };
	try {
		return new Intl.NumberFormat(locale, options).format(price);
	} catch {
		return new Intl.NumberFormat(FALLBACK_LOCALE, options).format(price);
	}
}
